import Map from 'ol/Map';
import View from 'ol/View';
import OSM from 'ol/source/OSM';
import Feature from 'ol/Feature';
import Polygon from 'ol/geom/Polygon';
import TileLayer from 'ol/layer/Tile';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import { Style, Fill, Stroke } from 'ol/style';

import { Boundary } from '../types/game-document';

export function generateZoneSnapshot(
  boundary: Boundary,
  width = 600,
  height = 280
): Promise<Blob> {
  return new Promise((resolve, reject) => {
    const container = document.createElement('div');
    container.style.cssText = `
      position: fixed;
      top: -9999px;
      left: -9999px;
      width: ${width}px;
      height: ${height}px;
      visibility: hidden;
    `;
    document.body.appendChild(container);

    const polygon = new Polygon(boundary.geometry.coordinates);
    const feature = new Feature({ geometry: polygon });
    const vectorSource = new VectorSource({ features: [feature] });

    const map = new Map({
      target: container,
      controls: [],
      interactions: [],
      layers: [
        new TileLayer({
          source: new OSM({
            crossOrigin: 'anonymous'
          })
        }),
        new VectorLayer({
          source: vectorSource,
          style: new Style({
            fill: new Fill({
              color: 'rgba(255, 255, 255, 0.2)'
            }),
            stroke: new Stroke({
              color: '#FF5527',
              width: 2
            })
          })
        })
      ],
      view: new View({ projection: 'EPSG:3857' })
    });

    const extent = vectorSource.getExtent();

    if (extent && !extent.some((v) => !isFinite(v))) {
      map.getView().fit(extent, {
        size: [width, height],
        padding: [32, 32, 32, 32],
        maxZoom: 18
      });
    }

    map.once('rendercomplete', () => {
      try {
        const exportCanvas = document.createElement('canvas');
        exportCanvas.width = width;
        exportCanvas.height = height;
        const ctx = exportCanvas.getContext('2d')!;

        container
          .querySelectorAll<HTMLCanvasElement>('.ol-layer canvas')
          .forEach((layerCanvas) => {
            if (layerCanvas.width === 0) return;

            const opacity =
              (layerCanvas.parentElement as HTMLElement)?.style?.opacity ?? '1';
            ctx.globalAlpha = opacity === '' ? 1 : parseFloat(opacity);

            const transform = layerCanvas.style.transform;
            const matrix = transform
              .match(/^matrix\(([^)]*)\)$/)?.[1]
              .split(',')
              .map(Number);
            if (matrix?.length === 6) {
              ctx.setTransform(
                ...(matrix as [number, number, number, number, number, number])
              );
            }
            ctx.drawImage(layerCanvas, 0, 0);
          });

        ctx.setTransform(1, 0, 0, 1, 0, 0);
        ctx.globalAlpha = 1;

        exportCanvas.toBlob(
          (blob) => {
            map.setTarget(undefined);
            document.body.removeChild(container);

            if (blob) resolve(blob);
            else reject(new Error('Canvas export returned null blob'));
          },
          'image/jpeg',
          0.85
        );
      } catch (err) {
        map.setTarget(undefined);
        document.body.removeChild(container);
        reject(err);
      }
    });
  });
}
